import * as React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Link as MuiLink,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import {deliveryResolutions, deliverySandbox, SAMPLE_LEAD} from "./portalRedirectCampaignsService";
import moment from "moment";

type Props = {
    post: any;
    setPost: (p: any) => void;
    resolutionTypes: string[];
};

export default function PostChecker({post, setPost, resolutionTypes}: Props) {
    const [sandboxOpen, setSandboxOpen] = React.useState(false);
    const [resOpen, setResOpen] = React.useState(false);
    const [execute, setExecute] = React.useState(false);
    const [fields, setFields] = React.useState<{field: string; sample: string}[]>([]);
    const [request, setRequest] = React.useState("");
    const [response, setResponse] = React.useState("");
    const [resolution, setResolution] = React.useState("");
    const [resInput, setResInput] = React.useState("");

    const update = (patch: any) => setPost({...post, ...patch});

    const resolutions = post.resolutions?.length ? post.resolutions : [{type: resolutionTypes[0], regex: "missing"}];

    const openSandbox = () => {
        const samples = {...SAMPLE_LEAD, current_date: moment().format("YYYY-MM-DD HH:mm:ss")};
        setFields(Object.keys(samples).map((field) => ({field, sample: samples[field]})));
        setRequest("");
        setResponse("");
        setResolution("");
        setExecute(false);
        setSandboxOpen(true);
    };

    const runSandbox = async () => {
        const result = await deliverySandbox(post, fields, execute);
        if (result) {
            setRequest(result.request || "");
            setResponse(result.response || "");
            setResolution(result.resolution || "");
        }
    };

    const runResolutions = async () => {
        const out = await deliveryResolutions(resInput, resolutions);
        setResolution(out || "");
    };

    return (
        <>
            <TextField multiline minRows={2} fullWidth label="URL" value={post.url || ""} onChange={(e) => update({url: e.target.value})} sx={{mb: 1}}/>
            <TextField multiline minRows={4} fullWidth label="Body" value={post.body || ""} onChange={(e) => update({body: e.target.value})} sx={{mb: 1}}/>
            <TextField multiline minRows={2} fullWidth label="Headers" value={post.headers || ""} onChange={(e) => update({headers: e.target.value})} sx={{mb: 1}}/>
            <Box sx={{mb: 1, display: "flex", gap: 2, flexWrap: "wrap", fontSize: 13}}>
                <MuiLink component="button" onClick={openSandbox}>Sandbox</MuiLink>
                <MuiLink href="https://github.com/Shopify/liquid/wiki/Liquid-for-Designers" target="_blank" rel="noreferrer">Liquid Markup</MuiLink>
                <MuiLink href="https://hackhands.com/format-datetime-ruby/" target="_blank" rel="noreferrer">Ruby Date/Time</MuiLink>
            </Box>
            <Typography variant="subtitle2">Resolution</Typography>
            {resolutions.map((r: any, i: number) => (
                <Box key={i} sx={{display: "flex", gap: 1, alignItems: "center", mb: 0.5}}>
                    <FormControl size="small" sx={{minWidth: 120}}>
                        <Select value={r.type || resolutionTypes[0]} onChange={(e) => {
                            const next = resolutions.map((x: any, idx: number) => idx === i ? {...x, type: e.target.value} : x);
                            update({resolutions: next});
                        }}>
                            {resolutionTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField size="small" value={r.regex || ""} onChange={(e) => {
                        const next = resolutions.map((x: any, idx: number) => idx === i ? {...x, regex: e.target.value} : x);
                        update({resolutions: next});
                    }}/>
                    {i === resolutions.length - 1 && (
                        <AddCircleIcon color="success" sx={{cursor: "pointer"}} onClick={() => update({resolutions: [...resolutions, {type: resolutionTypes[0], regex: ""}]})}/>
                    )}
                    {resolutions.length > 1 && (
                        <DeleteIcon color="error" sx={{cursor: "pointer"}} onClick={() => update({resolutions: resolutions.filter((_: any, idx: number) => idx !== i)})}/>
                    )}
                </Box>
            ))}
            <Box sx={{mt: 1}}>
                <MuiLink component="button" onClick={() => { setResInput(""); setResolution(""); setResOpen(true); }}>Sandbox</MuiLink>
            </Box>

            <Dialog open={sandboxOpen} onClose={() => setSandboxOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Post Sandbox</DialogTitle>
                <DialogContent>
                    {request ? <TextField fullWidth multiline minRows={4} label="Request" value={request} sx={{mt: 1}} InputProps={{readOnly: true}}/> : null}
                    {response ? <TextField fullWidth multiline minRows={4} label="Response" value={response} sx={{mt: 1}} InputProps={{readOnly: true}}/> : null}
                    {resolution ? <TextField fullWidth multiline minRows={3} label="Resolution" value={resolution} sx={{mt: 1}} InputProps={{readOnly: true}}/> : null}
                    <FormControlLabel control={<Switch checked={execute} onChange={(e) => setExecute(e.target.checked)}/>} label="Execute Mode" sx={{my: 1}}/>
                    {fields.map((item, i) => (
                        <Box key={item.field} sx={{display: "flex", gap: 1, alignItems: "center", mb: 0.5}}>
                            <Box sx={{width: 180, fontSize: 13}}>{item.field}</Box>
                            <TextField size="small" fullWidth value={item.sample} onChange={(e) => {
                                const next = fields.map((x, idx) => idx === i ? {...x, sample: e.target.value} : x);
                                setFields(next);
                            }}/>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSandboxOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={runSandbox}>Test</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={resOpen} onClose={() => setResOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Resolution Sandbox</DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline minRows={5} label="Response" value={resInput} onChange={(e) => setResInput(e.target.value)} sx={{mt: 1}}/>
                    {resolution ? <TextField fullWidth multiline minRows={3} label="Resolution" value={resolution} sx={{mt: 1}} InputProps={{readOnly: true}}/> : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={runResolutions}>Test</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
